/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/
import {bundlingSettingsConfiguration} from './configurations/bundling-settings.configuration';
import {StatefulComponent} from '../../shared/stateful/stateful.component';
import {BundlingSettingsState} from './models/bundling-settings.state';
import {BundlingSettingsMapper} from './mappers/bundling-settings.mapper';
import {datePipe} from '../util/bundle-data-pipes';
import {BundlesConfigService} from '../services/bundles-config.service';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {BundlingSettingsDetailModel} from '../bundles-main-page/models/bundling-settings-detail/bundling-settings-detail.model';
import {BundlingSettingsCustomerBundlesTabsModel} from '../bundles-main-page/models/bundling-settings-tabs/bundling-settings-customer-bundles-tabs.model';
import {finalize} from 'rxjs/operators';
import {SpinnerContextTypeEnum} from '../../shared/spinner/spinner-context-type.enum';
import {BundlingSettingsDetailContract} from '../api/contracts/bundle-settings/bundling-settings-detail.contract';
import {BundlingSettingsDetailMapper} from '../bundles-main-page/mappers/bundling-settings-detail/bundling-settings-detail.mapper';
import {SpinnerService} from '../../shared/spinner/spinner.service';
import {BundlesApi} from '../api/bundles.api';
import {
    Subject,
    Subscription
} from 'rxjs';
import {BundlingSettingsCustomerBundlesActiveTabsModel} from '../bundles-main-page/models/bundling-settings-tabs/bundling-settings-customer-bundles-active-tabs.model';
import {getCurrencySymbol} from '@angular/common';
import {BundlingSettingsDropdownsModel} from '../bundles-main-page/models/bundling-settings-dropdowns/bundling-settings-dropdowns.model';
import {BundlingSettingsInputEnum} from './models/bundling-settings-input.enum';
import {ConfirmModalComponent} from '../../shared/confirm-modal/confirm-modal/confirm-modal.component';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {fastDeepCopy} from '../../shared/utils/deep-copy';
import {BundlingSettingsEditedFormDataModel} from './models/bundling-settings-edited-form-data.model';
import {InputToggleTextComponent} from '../shared/components/bundling-settings-inputs/input-toggle-text/input-toggle-text.component';
import {ToastWrapperService} from '../../shared/utils/toast-wrapper-service/toast-wrapper-service';
import bundlingSettingsModals from './configurations/bundling-settings-modals.configuration';
import {UnsavedChangesService} from '../../shared/guards/unsaved-work/unsaved-changes.service';
import {ReplayableRouterEventsService} from '../../shared/services/replayable-router-events.service';
import {BundlingSettingsInputsEmitterModel} from '../shared/components/bundling-settings-inputs/models/bundling-settings-inputs-emitter.model';
import {BundlingSettingsEditFormState} from './models/bundling-settings-edit-form-state';

@Component({
    selector: 'app-bundling-settings',
    templateUrl: './bundling-settings.component.html',
    styleUrls: ['./bundling-settings.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BundlingSettingsComponent extends StatefulComponent<BundlingSettingsState> implements OnInit {
    @Input() formData: BundlingSettingsDetailModel;
    @Input() isEditMode: boolean;
    @Input() tabsInfo: BundlingSettingsCustomerBundlesTabsModel;
    @Input() activeTabs: BundlingSettingsCustomerBundlesActiveTabsModel;
    @Input() formDropdownsData: BundlingSettingsDropdownsModel;
    @Output() closeForm = new EventEmitter<boolean>();
    @Output() editedFormData = new EventEmitter<boolean>();
    @Output() reloadPage = new EventEmitter<void>();
    private subscriptions = new Subscription();
    private applySettingsModalLoadingSubject = new Subject<boolean>();
    private disableSettingsModalLoadingSubject = new Subject<boolean>();
    private readonly LOCAL_LOADING_INSTANCE_KEY = 'BUNDLING_SETTINGS_CONTENT_LOADING';
    private readonly BUNDLES_MAX_VALUE_CONFIG_KEY = 'maxValue';
    public spinnerObservable = this.spinnerService.onLocalChange(this.LOCAL_LOADING_INSTANCE_KEY);
    public bundlingSettings = bundlingSettingsConfiguration;
    public bundlingSettingsInitialData: BundlingSettingsEditedFormDataModel;
    public changeTabActiveCustomerCode = new EventEmitter<string>();
    public changeTabsActiveBundleTypeCode = new EventEmitter<string>();
    @ViewChild('bundleMaxPositionsEditModeInput') bundleMaxPositionsEditMode: InputToggleTextComponent;

    constructor(public bundlesConfigService: BundlesConfigService,
                public spinnerService: SpinnerService,
                public bundleService: BundlesApi,
                private replay: ReplayableRouterEventsService,
                private modalService: NgbModal,
                private unsavedChangesService: UnsavedChangesService,
                private cdr: ChangeDetectorRef,
                private toast: ToastWrapperService) {
        super();
    }

    ngOnInit(): void {
        this.storeState({
            customerCode: this.activeTabs?.tabActiveCustomerCode,
            bundleTypeCode: this.activeTabs?.tabsActiveBundleTypeCode,
            currency: getCurrencySymbol(this.bundlesConfigService.currency?.alphabeticCode, 'narrow'),
            bundlingSettingsDetail: this.formData
        });
        this.storeState({isValidToApply: false});
        this.updateFormData(this.formData, this.formData.active);
        if (this.isEditMode) {
            this.unsavedChangesService.originalValue = this.bundlingSettingsInitialData;
        }
    }

    emitCloseForm(): void {
        this.disableGuardsOnCloseBundlingSettings();
        this.closeForm.emit(true);
    }

    customerTabEmit(customerTabEmit: string): void {
        if (this.isEditMode && this.state.isValidToApply) {
            const discardModal = this.discardAction();
            // The modal has a primary confirmation event and a secondary cancellation event. To ensure proper functionality,
            // it is imperative that the selected tab only changes upon confirmation, while cancellation retains the current tab.
            discardModal.primaryEvent.subscribe(() => {
                this.storeState({customerCode: customerTabEmit});
                this.getBundlingSettingsConfiguration(customerTabEmit, this.state.bundleTypeCode);
                this.changeTabActiveCustomerCode.emit(customerTabEmit);
            });
        } else {
            this.storeState({customerCode: customerTabEmit});
            this.getBundlingSettingsConfiguration(customerTabEmit, this.state.bundleTypeCode);
            this.changeTabActiveCustomerCode.emit(customerTabEmit);
        }
        if (this.isEditMode) {
            this.unsavedChangesService.originalValue = this.bundlingSettingsInitialData;
        }
    }

    bundleTypeEmit(bundleTypeCodeEmit: string): void {
        this.unsavedChangesService.originalValue = this.state.isValidToApply;
        if (this.isEditMode && this.state.isValidToApply) {
            const discardModal = this.discardAction();
            // The modal has a primary confirmation event and a secondary cancellation event. To ensure proper functionality,
            // it is imperative that the selected tab only changes upon confirmation, while cancellation retains the current tab.
            discardModal.primaryEvent.subscribe(() => {
                this.storeState({bundleTypeCode: bundleTypeCodeEmit});
                this.getBundlingSettingsConfiguration(this.state.customerCode, bundleTypeCodeEmit);
                this.changeTabsActiveBundleTypeCode.emit(bundleTypeCodeEmit);
            });
        } else {
            this.storeState({bundleTypeCode: bundleTypeCodeEmit});
            this.getBundlingSettingsConfiguration(this.state.customerCode, bundleTypeCodeEmit);
            this.changeTabsActiveBundleTypeCode.emit(bundleTypeCodeEmit);
        }
        if (this.isEditMode) {
            this.unsavedChangesService.originalValue = this.bundlingSettingsInitialData;
        }
    }

    enableApplyButton(): void {
        this.storeState({
            isValidToApply:
                    this.state.editedFormData.active !== this.bundlingSettingsInitialData.active && this.editedFormDataInputsIsValid()
                    || JSON.stringify(this.state.editedFormData) !== JSON.stringify(this.bundlingSettingsInitialData) &&
                    this.state.editedFormData.periodicity?.value != null && this.editedFormDataInputsIsValid()
        });

    }

    formInputValueHandler(bundleSettingsInput: BundlingSettingsInputsEmitterModel): void {
        switch (bundleSettingsInput.code) {
            case BundlingSettingsInputEnum.PERIODICITY:
                this.storeState({
                    editedFormData: {
                        ...this.state.editedFormData,
                        periodicity: this.mapInputValue(bundleSettingsInput)
                    }
                });
                break;
            case BundlingSettingsInputEnum.PERIOD_DAYS_OFFSET:
                this.storeState({
                    editedFormData: {
                        ...this.state.editedFormData,
                        periodDaysOffset: this.mapInputValue(bundleSettingsInput)
                    }
                });
                break;
            case BundlingSettingsInputEnum.PERIOD_MAX_BUNDLES:
                this.storeState({
                    editedFormData: {
                        ...this.state.editedFormData,
                        periodMaxBundles: this.mapInputValue(bundleSettingsInput)
                    }
                });
                break;
            case BundlingSettingsInputEnum.MAX_BUNDLE_LINES:
                this.storeState({
                    editedFormData: {
                        ...this.state.editedFormData,
                        bundleMaxPositions: this.mapInputValue(bundleSettingsInput)
                    }
                });
                break;
            case BundlingSettingsInputEnum.BUNDLE_MAX_VALUE:
                const warning = this.bundleMaxValueCustomWarningHandler(bundleSettingsInput.value, bundleSettingsInput);
                this.storeState({
                    editedFormData: {
                        ...this.state.editedFormData,
                        bundleMaxValue: this.mapInputValue(bundleSettingsInput)
                    },
                    maxBundleValueCustomWarning: warning
                });
                break;
            case BundlingSettingsInputEnum.BUNDLE_AGGREGATION:
                this.storeState({
                    editedFormData: {
                        ...this.state.editedFormData,
                        bundleCriterionSpec: this.mapInputValue(bundleSettingsInput)
                    }
                });
                break;
            case BundlingSettingsInputEnum.LINE_AGGREGATION:
                this.storeState({
                    editedFormData: {
                        ...this.state.editedFormData,
                        bundlePositionCriterionSpec: this.mapInputValue(bundleSettingsInput)
                    }
                });
                this.changeBundleMaxPositionsInput(bundleSettingsInput);
                break;
        }
        if (this.isEditMode) {
            this.subscriptions.add(
                    this.replay.onGuardsCheckStart.subscribe(() => {
                        this.updateCurrentState();
                    })
            );
        }
        this.enableApplyButton();
    }

    mapInputValue(bundleSettingsInput: BundlingSettingsInputsEmitterModel): BundlingSettingsEditFormState {
        return {
            value: bundleSettingsInput.value === undefined ? undefined : bundleSettingsInput.value?.toString(),
            isValid: bundleSettingsInput.isValid
        };
    }

    bundlingSwitchToggleHandler(active: boolean): void {
        this.updateFormData(this.state.bundlingSettingsDetail, active);
        this.storeState({editedFormData: {...this.state.editedFormData, active: active}});
    }

    applyBundlingSettings(): void {
        if (this.state.bundlingSwitchButtonModel.checked) {
            this.applyBusinessSettingsModal();
        } else {
            this.disableBusinessSettingsModal();
        }
    }

    closeBundlingSettings(): void {
        if (this.isEditMode && this.state.isValidToApply) {
            const discardModal = this.discardAction();
            discardModal.primaryEvent.subscribe(() => {
                this.emitCloseForm();
            });
        } else {
            this.emitCloseForm();
        }
    }

    private disableGuardsOnCloseBundlingSettings(): void {
        this.subscriptions.add(
                this.replay.onGuardsCheckStart.subscribe(() => {
                    this.unsavedChangesService.ignoreChanges();
                })
        );
    }

    private editedFormDataInputsIsValid(): boolean {
        return this.state.editedFormData.periodicity?.isValid &&
                this.state.editedFormData.periodMaxBundles?.isValid &&
                this.state.editedFormData.bundleMaxValue?.isValid &&
                this.state.editedFormData.periodDaysOffset?.isValid &&
                this.state.editedFormData.bundleCriterionSpec?.isValid &&
                this.state.editedFormData.bundlePositionCriterionSpec?.isValid &&
                this.state.editedFormData.bundleMaxPositions?.isValid;
    }

    private discardAction(): ConfirmModalComponent {
        const discardModalConfiguration: ConfirmModalComponent = this.modalService.open(ConfirmModalComponent).componentInstance;
        discardModalConfiguration.modalConfiguration = {
            ...bundlingSettingsModals.discardSettingsModalConfiguration
        };
        return discardModalConfiguration;
    }

    private changeBundleMaxPositionsInput(bundleSettingsInput: BundlingSettingsInputsEmitterModel): void {
        if (bundleSettingsInput.value != null && bundleSettingsInput.isValid) {
            this.bundleMaxPositionsEditMode.textInput?.enable();
            this.bundleMaxPositionsEditMode.textInput?.setValue(this.bundlingSettingsInitialData.bundleMaxPositions?.value ?? '');
        } else if (bundleSettingsInput.value === undefined) {
            this.bundleMaxPositionsEditMode?.textInput?.disable();
            this.bundleMaxPositionsEditMode?.textInput?.setValue(undefined);
        }
        this.cdr.detectChanges();
    }

    private applyBusinessSettingsModal(): void {
        const acceptModalConfiguration: ConfirmModalComponent = this.modalService.open(ConfirmModalComponent).componentInstance;
        const activeCustomerName =
                this.tabsInfo?.tabsCustomerCodes.find((tab) => tab.tabCode === this.state.customerCode).tabName;
        const activeBundleTypeName =
                this.tabsInfo?.tabsBundleTypesCodes.find((tab) => tab.tabCode === this.state.bundleTypeCode).tabName;
        // eslint-disable-next-line max-len
        const checkOptionSecond = `The settings that you're about to confirm on ${activeCustomerName} ${activeBundleTypeName} will just be applied when a new period is created.`;
        bundlingSettingsModals.applySettings.body.manualChecks.options = [];

        if (this.state.isNewSettingsConfiguration || !this.bundlingSettingsInitialData.active) {
            const checkOptionFirst = `The ${activeBundleTypeName} will be activated.`;
            bundlingSettingsModals.applySettings.body.manualChecks.options.push(checkOptionFirst);
        }
        bundlingSettingsModals.applySettings.body.manualChecks.options.push(checkOptionSecond);

        acceptModalConfiguration.modalConfiguration = {
            ...bundlingSettingsModals.applySettings,
            isLoading: this.applySettingsModalLoadingSubject
        };

        acceptModalConfiguration.primaryEvent.subscribe(() => {
            this.applyBusinessSettingsAction(this.state.customerCode, this.state.bundleTypeCode);
            this.applySettingsModalLoadingSubject.next(true);
        });
    }

    private disableBusinessSettingsModal(): void {
        const disableModalConfiguration: ConfirmModalComponent = this.modalService.open(ConfirmModalComponent).componentInstance;
        const activeCustomerName =
                this.tabsInfo?.tabsCustomerCodes.find((tab) => tab.tabCode === this.state.customerCode).tabName;
        const activeBundleTypeName =
                this.tabsInfo?.tabsBundleTypesCodes.find((tab) => tab.tabCode === this.state.bundleTypeCode).tabName;
        const checkOption = `The ${activeCustomerName} ${activeBundleTypeName} will be inactivated. `
                + `Be aware that, after the last active period is concluded, `
                + `no more settlements will be integrated and no more periods created.`;

        bundlingSettingsModals.deactivateSettings.body.manualChecks.options = [];
        bundlingSettingsModals.deactivateSettings.body.manualChecks.options.push(checkOption);

        disableModalConfiguration.modalConfiguration = {
            ...bundlingSettingsModals.deactivateSettings,
            isLoading: this.disableSettingsModalLoadingSubject
        };

        disableModalConfiguration.primaryEvent.subscribe(() => {
            this.disableBusinessSettings(this.state.customerCode, this.state.bundleTypeCode);
            this.disableSettingsModalLoadingSubject.next(true);
        });
    }

    private applyBusinessSettingsAction(customerCode: string, bundleTypeCode: string): void {
        const activeCustomerName =
                this.tabsInfo.tabsCustomerCodes.find((tab) => tab.tabCode === this.state.customerCode).tabName;
        const activeBundleTypeName =
                this.tabsInfo.tabsBundleTypesCodes.find((tab) => tab.tabCode === this.state.bundleTypeCode).tabName;
        const data = BundlingSettingsMapper.fromBundlingSettingsEditedFormDataModelToBundlingSettingsContract(this.state.editedFormData);
        this.spinnerService.start(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
        this.subscriptions.add(
                this.bundleService.createBundlingSettings(customerCode, bundleTypeCode, data)
                        .subscribe(
                                () => this.onSuccessApply(activeCustomerName, activeBundleTypeName),
                                () => {
                                    this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
                                },
                                () => {
                                    this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
                                }));
    }

    private onSuccessApply(activeCustomerName: string, activeBundleTypeName: string): void {
        this.emitCloseForm();
        if (this.state.isNewSettingsConfiguration || !this.bundlingSettingsInitialData.active) {
            this.toast.success(`<li>The ${activeBundleTypeName} on ${activeCustomerName} was activated with success.</li>`
                    + `<li>${this.textApplyMessageToast(activeCustomerName, activeBundleTypeName)} </li>`
                    + `<b>${bundlingSettingsModals.defaultModalText}</b>`, 'Success');
        } else {
            this.toast.success(
                    `${this.textApplyMessageToast(activeCustomerName, activeBundleTypeName)} `
                    + `<b>${bundlingSettingsModals.defaultModalText}</b>`,
                    'Success');
        }
        this.reloadPage.emit();
    }

    private textApplyMessageToast(activeCustomerName: string, activeBundleTypeName: string): string {
        return `The new settings on ${activeCustomerName} ${activeBundleTypeName} were updated with success.`;
    }

    private disableBusinessSettings(customerCode: string, bundleTypeCode: string): void {
        const activeCustomerName =
                this.tabsInfo.tabsCustomerCodes.find((tab) => tab.tabCode === this.state.customerCode).tabName;
        const activeBundleTypeName =
                this.tabsInfo.tabsBundleTypesCodes.find((tab) => tab.tabCode === this.state.bundleTypeCode).tabName;
        this.spinnerService.start(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
        this.subscriptions.add(
                this.bundleService.disableBundlingSettings(customerCode, bundleTypeCode)
                        .subscribe(
                                () => {
                                    this.onSuccessDisable(activeCustomerName, activeBundleTypeName);
                                },
                                () => {
                                    this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
                                },
                                () => {
                                    this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
                                }
                        ));
    }

    private onSuccessDisable(activeCustomerName: string, activeBundleTypeName: string): void {
        this.emitCloseForm();
        this.toast.success(`The ${activeCustomerName} ${activeBundleTypeName} inactivation was successfully concluded. `
                + `<b>Be aware that, after the last active period is concluded, `
                + `no more settlements will be integrated and no more periods created.</b>`,
                'Success');
        this.reloadPage.emit();
    }

    private getBundlingSettingsConfiguration(customerCode: string, bundleTypeCode: string): void {
        this.spinnerService.start(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
        this.subscriptions.add(
                this.bundleService.getBundlesSettings(customerCode, bundleTypeCode)
                        .pipe(finalize(() => {
                            this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
                        }))
                        .subscribe((bundlingSettingsDetail: BundlingSettingsDetailContract) => {
                            this.storeState({
                                bundlingSettingsDetail: BundlingSettingsDetailMapper
                                        .fromBundlingSettingsDetailContractToBundlingSettingsDetailModel(bundlingSettingsDetail)
                            });
                            this.updateFormData(this.state.bundlingSettingsDetail, this.state.bundlingSettingsDetail?.active);
                        }));
    }

    private isNewSettingsConfiguration(data: BundlingSettingsDetailModel): boolean {
        return data?.lastChangedBy == null;
    }

    private updateFormData(data: BundlingSettingsDetailModel, isBundlingSwitchToggleActive: boolean): void {
        this.storeState({
            isNewSettingsConfiguration: this.isNewSettingsConfiguration(data),
            bundlingSwitchButtonModel: {
                checked: isBundlingSwitchToggleActive,
                disabled: !this.isEditMode
            },
            periodicityModel: BundlingSettingsMapper
                    .fromFormDataValueToInputToggleDropdownModel(data?.periodicity?.name,
                            this.isEditMode,
                            isBundlingSwitchToggleActive,
                            this.isToggleDisable(isBundlingSwitchToggleActive)),
            periodDaysOffsetSwitchModel: BundlingSettingsMapper
                    .fromFormDataValueToInputToggleTextModel(data?.periodDaysOffset,
                            this.isEditMode,
                            this.isToggleActive(data?.periodDaysOffset, isBundlingSwitchToggleActive),
                            this.isToggleDisable(isBundlingSwitchToggleActive)),
            periodMaxBundlesSwitchModel: BundlingSettingsMapper
                    .fromFormDataValueToInputToggleTextModel(data?.periodMaxBundles,
                            this.isEditMode,
                            this.isToggleActive(data?.periodMaxBundles, isBundlingSwitchToggleActive),
                            this.isToggleDisable(isBundlingSwitchToggleActive)),
            bundleMaxValueSwitchModel: BundlingSettingsMapper
                    .fromFormDataValueToInputToggleTextModel(data?.bundleMaxValue,
                            this.isEditMode,
                            this.isToggleActive(data?.bundleMaxValue, isBundlingSwitchToggleActive),
                            this.isToggleDisable(isBundlingSwitchToggleActive)),
            bundleAggregationSwitchModel: BundlingSettingsMapper
                    .fromFormDataValueToInputToggleDropdownModel(data?.bundleCriterion?.name,
                            this.isEditMode,
                            this.isToggleActive(data?.bundleCriterion?.name, isBundlingSwitchToggleActive),
                            this.isToggleDisable(isBundlingSwitchToggleActive)),
            lineAggregationSwitchModel: BundlingSettingsMapper
                    .fromFormDataValueToInputToggleDropdownModel(data?.bundlePositionCriterion?.name,
                            this.isEditMode,
                            this.isToggleActive(data?.bundlePositionCriterion?.name, isBundlingSwitchToggleActive),
                            this.isToggleDisable(isBundlingSwitchToggleActive)),
            bundleMaxPositions: BundlingSettingsMapper
                    .fromFormDataValueToInputToggleTextModel(data?.bundleMaxPositions,
                            this.isEditMode,
                            this.isToggleActive(data?.bundleMaxPositions, isBundlingSwitchToggleActive),
                            this.isToggleDisable(isBundlingSwitchToggleActive)),
            lastUpdatedBy: data?.lastChangedBy,
            lastUpdatedOnDate: this.convertLastUpdatedOnToDate(data?.lastChangedOn),
            editedFormData: BundlingSettingsMapper
                    .fromBundlingSettingsDetailModelToBundlingSettingsEditedFormDataModel(data,
                            this.bundlesConfigService.currency?.alphabeticCode),
            maxBundleValueCustomWarning: this.bundleMaxValueCustomWarningHandler(data?.bundleMaxValue?.toString())
        });
        this.bundlingSettingsInitialData = fastDeepCopy(this.state.editedFormData);
    }

    private isToggleActive(input: string | number, isBundlingToggleActive: boolean): boolean {
        return input != null && isBundlingToggleActive;
    }

    private isToggleDisable(isBundlingToggleActive: boolean): boolean {
        return !isBundlingToggleActive;
    }

    private convertLastUpdatedOnToDate(timestamp: number): string {
        return datePipe({
            date: timestamp,
            timezone: this.bundlesConfigService.timezone
        });
    }

    private updateCurrentState(): void {
        this.unsavedChangesService.currentValue = this.state.editedFormData;
    }

    private bundleMaxValueCustomWarningHandler(maxValue: string, bundleSettingsInput?: BundlingSettingsInputsEmitterModel): string {
        let message: string;
        const maxBundleAmount = this.getBundleMaxTotalAmountConfig();

        if (maxBundleAmount != null && !isNaN(maxBundleAmount)) {
            const numericValue = +maxValue;
            if (this.isEmpty(maxValue)) {
                message = this.mandatoryBundleMaxValueInputCustomWarning(maxBundleAmount);
            } else if (!isNaN(numericValue)
                    && numericValue < maxBundleAmount
                    && !maxValue?.startsWith?.('0')) {
                message = this.infoBundleMaxValueInputCustomWarning(maxBundleAmount);
            }
        }

        this.evaluateInputInvalidation(message, bundleSettingsInput);

        return message;
    }

    private getBundleMaxTotalAmountConfig(): number {
        return +this.bundlesConfigService.customerBundleTypeConfigs?.find(
                (customerBundleTypeConfig) => customerBundleTypeConfig.bundleTypeCode === this.state.bundleTypeCode
                        && customerBundleTypeConfig.customerCode === this.state.customerCode)
                ?.configurations
                ?.find((config) => config.key === this.BUNDLES_MAX_VALUE_CONFIG_KEY)?.value;
    }

    private infoBundleMaxValueInputCustomWarning(maxBundleAmount: number): string {
        return `The Max Amount by bundle must be greater or equal than ${maxBundleAmount} ¥ `
                + `defined on Settlements module. Please contact ORCAN dev team to support you on this.`;
    };

    private mandatoryBundleMaxValueInputCustomWarning(maxBundleAmount: number): string {
        return `Mandatory field to apply. The Max Amount by bundle must be greater or `
                + `equal than ${maxBundleAmount} ¥ defined on Settlements module.`;
    };

    private evaluateInputInvalidation(message: string, bundleSettingsInput: BundlingSettingsInputsEmitterModel): void {
        if (!this.isEmpty(message) && bundleSettingsInput != null && !bundleSettingsInput.isDisabled) {
            bundleSettingsInput.isValid = false;
        }
    }

    private isEmpty(value: string): boolean {
        return value == null || value === '';
    };
}
